<template>
  <div class="payment-page">
    <AppHeader />
    <div class="payment-container">
      <div class="payment-form">
        <div class="payment-header">
           <h1>{{ $t('legacy.Покупка кредитов') }}</h1>
            <p v-if="credits">{{ $t('legacy.dynamic.selectedCredits', { credits }) }}</p>
           <p class="payment-amount">
             {{ $t('legacy.Сумма к оплате:') }}
             <strong v-if="paymentQuote">{{ formatQuoteAmount(paymentQuote.amount, paymentQuote.currency) }}</strong>
             <span v-else-if="quoteLoading">{{ $t('legacy.paymentQuoteLoading') }}</span>
             <span v-else-if="quoteError" class="provider-error">{{ quoteError }}</span>
             <span v-else>—</span>
           </p>
        </div>

        <div class="provider-section">
          <h3>{{ $t('legacy.paymentProviderTitle') }}</h3>
          <p class="provider-hint">{{ $t('legacy.paymentProviderHint') }}</p>
          <label class="provider-option" :class="{ selected: provider === 'yookassa' }">
            <input v-model="provider" type="radio" value="yookassa" :disabled="providersLoading || !!providersError" @change="loadPaymentQuote" />
            <span>
              <strong>{{ $t('legacy.paymentProviderRussia') }}</strong>
              <small>{{ $t('legacy.paymentProviderYooKassa') }}</small>
            </span>
          </label>
          <label class="provider-option" :class="{ selected: provider === 'prodamus', unavailable: !providers.prodamus }">
            <input v-model="provider" type="radio" value="prodamus" :disabled="providersLoading || !!providersError || !providers.prodamus" @change="loadPaymentQuote" />
            <span>
              <strong>{{ $t('legacy.paymentProviderOutsideRussia') }}</strong>
              <small v-if="providers.prodamus">
                {{ $t('legacy.paymentProviderProdamus') }}<span v-if="prodamusCurrency"> · {{ prodamusCurrency }}</span>
              </small>
              <small v-else>{{ $t('legacy.paymentProviderUnavailable') }}</small>
            </span>
          </label>
          <p v-if="providersLoading" class="provider-state">{{ $t('legacy.paymentProvidersLoading') }}</p>
          <p v-else-if="providersError" class="provider-error">{{ providersError }}</p>
        </div>

        <div v-if="provider" class="payment-info">{{ provider === 'prodamus' ? $t('legacy.redirectToProdamus') : $t('legacy.redirectToYooKassa') }}</div>
        <button class="pay-btn" @click="initPayment" :disabled="processing || providersLoading || !!providersError || !provider || !paymentQuote || quoteLoading || !!quoteError || (provider === 'prodamus' && !providers.prodamus)">
          {{ processing ? $t('legacy.Создание платежа...') : $t('legacy.Перейти к оплате') }}
        </button>
        <p v-if="error" class="provider-error">{{ error }}</p>
        <div class="back-link">
           <router-link to="/projects">← {{ $t('legacy.Вернуться к проектам') }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import { createCreditPayment, getPaymentProviders, getPaymentQuote } from '@/api/payments';
import { useToast } from 'vue-toastification';
import { paymentErrorMessage, paymentResponseError } from '@/utils/paymentError';

export default {
  name: 'CreditsPaymentPage',
  components: {
    AppHeader,
  },
  data() {
    return {
      packageId: null,
      credits: null,
      processing: false,
      toast: null,
      providers: { prodamus: false },
      prodamusCurrency: null,
      provider: null,
      providersLoading: true,
      providersError: null,
      paymentQuote: null,
      quoteLoading: false,
      quoteError: null,
      quoteRequestSequence: 0,
      error: null,
    };
  },
  async created() {
    this.toast = useToast();
    this.packageId = this.$route.query.packageId;
    
    if (typeof this.packageId !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(this.packageId)) {
       this.toast.error(this.$t('legacy.Пакет кредитов не выбран'));
      this.$router.push('/projects');
      return;
    }
    await this.loadPaymentProviders();
  },
  methods: {
    async loadPaymentProviders() {
      this.providersLoading = true;
      this.providersError = null;
      try {
        const result = await getPaymentProviders();
        this.providers = { prodamus: result?.prodamus?.available === true };
        this.prodamusCurrency = result?.prodamus?.currency || null;
      } catch (error) {
        this.providersError = paymentResponseError(error, this.$t.bind(this), 'legacy.paymentProvidersError');
      } finally {
        this.providersLoading = false;
      }
    },
    async loadPaymentQuote() {
      const requestSequence = ++this.quoteRequestSequence;
      this.paymentQuote = null;
      this.credits = null;
      this.quoteError = null;
      if (!this.packageId || !this.provider || (this.provider === 'prodamus' && !this.providers.prodamus)) {
        this.quoteLoading = false;
        return;
      }

      this.quoteLoading = true;
      try {
        const quote = await getPaymentQuote(`credits_${this.packageId}`, this.provider);
        if (requestSequence === this.quoteRequestSequence) {
          if (!Number.isSafeInteger(quote.credits) || quote.credits <= 0) {
            this.quoteError = this.$t('legacy.paymentQuoteError');
            return;
          }
          this.paymentQuote = quote;
          this.credits = quote.credits;
        }
      } catch (error) {
        if (requestSequence === this.quoteRequestSequence) {
          this.quoteError = paymentResponseError(error, this.$t.bind(this), 'legacy.paymentQuoteError');
        }
      } finally {
        if (requestSequence === this.quoteRequestSequence) {
          this.quoteLoading = false;
        }
      }
    },
    formatQuoteAmount(amount, currency) {
      if (amount === undefined || amount === null || !currency) return '—';
      return this.$formatCurrency(Number(amount), currency, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    async initPayment() {
      if (this.processing || this.providersLoading || this.providersError || !this.provider || !this.paymentQuote || this.quoteLoading || this.quoteError || (this.provider === 'prodamus' && !this.providers.prodamus)) return;
      this.processing = true;
      this.error = null;
      try {
        const returnUrl = `${window.location.origin}/payment/return`;
        const result = await createCreditPayment(this.packageId, this.paymentQuote.amount, returnUrl, this.provider);
        if (result.confirmationUrl) {
          window.location.href = result.confirmationUrl;
        } else {
          this.error = this.$t('legacy.Не удалось получить ссылку на оплату');
        }
      } catch (error) {
        this.error = paymentErrorMessage(error, this.$t.bind(this), this.provider);
        this.toast.error(this.error);
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.payment-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa, #e8ecef);
}

.payment-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 40px 20px;
}

.payment-form {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.payment-header {
  text-align: center;
  margin-bottom: 32px;
}

.payment-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px 0;
}

.payment-header p {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.payment-amount {
  font-size: 18px;
}

.payment-amount strong {
  color: #f59e0b;
}

.provider-section {
  margin: 0 0 20px;
}

.provider-section h3 {
  margin: 0 0 6px;
  color: #333;
  font-size: 16px;
}

.provider-hint,
.provider-state {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
}

.provider-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
}

.provider-option.selected {
  border-color: #f59e0b;
  background: #fff8e1;
}

.provider-option.unavailable {
  opacity: 0.55;
  cursor: not-allowed;
}

.provider-option input {
  accent-color: #f59e0b;
}

.provider-option span {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #333;
}

.provider-option small {
  color: #777;
  font-size: 12px;
}

.provider-note {
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff8e1;
  color: #755b19;
  font-size: 13px;
  line-height: 1.45;
}

.provider-error {
  margin: 8px 0;
  color: #e74c3c;
  font-size: 13px;
}

.payment-info {
  margin: 0 0 18px;
  padding: 14px;
  border-radius: 8px;
  background: #fff8e1;
  color: #665522;
  font-size: 14px;
}

.pay-btn {
  width: 100%;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.pay-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.payment-mock {
  margin-bottom: 24px;
}

.mock-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff8e1;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #666;
}

.mock-notice svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.mock-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.success-btn {
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #4CAF50, #66BB6A);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.success-btn:hover {
  background: linear-gradient(135deg, #43A047, #5CB860);
  transform: translateY(-1px);
}

.error-btn {
  width: 100%;
  padding: 16px 24px;
  background: #f0f0f0;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.error-btn:hover {
  background: #e0e0e0;
}

.back-link {
  text-align: center;
  margin-top: 16px;
}

.back-link a {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.back-link a:hover {
  color: #f59e0b;
}

.payment-result {
  background: #fff;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.result-icon {
  margin-bottom: 24px;
}

.payment-result h2 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.payment-result.success h2 {
  color: #4CAF50;
}

.payment-result.error h2 {
  color: #F44336;
}

.payment-result p {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.balance-info {
  font-weight: 500;
  color: #f59e0b !important;
  margin-bottom: 24px !important;
}

.balance-info strong {
  color: #d97706;
}

.continue-btn {
  display: inline-block;
  padding: 14px 32px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;
  margin-top: 16px;
}

.continue-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.retry-btn {
  display: inline-block;
  padding: 14px 32px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 16px;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.back-btn {
  display: block;
  margin-top: 16px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
}

.back-btn:hover {
  color: #f59e0b;
}

@media (max-width: 768px) {
  .payment-container {
    padding: 20px 16px;
  }

  .payment-form {
    padding: 24px 20px;
  }
}
</style>
